import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, TrendingUp, Layout, Video, Laptop, Target, Mail, Search, Share2, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, stats } from '../data/mock';

const iconMap = {
  TrendingUp, Layout, Video, Laptop, Target, Mail, Search, Share2, BarChart3
};

const Home = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-600 rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left space-y-8">
              <div className="inline-block">
                <span className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium backdrop-blur-sm">
                  🏌️ Your Local Golf Marketing Agency
                </span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
                Reach Your
                <span className="block bg-gradient-to-r from-cyan-400 to-teal-500 bg-clip-text text-transparent">
                  Maximum Potential
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed max-w-2xl">
                With more than 15 years of experience working within the golf industry, across apps, websites and talent management. We develop engaging marketing strategies and implement them to maximize your results.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white font-semibold px-8 py-6 text-lg rounded-xl shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 group">
                    Get Started
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/case-studies">
                  <Button variant="outline" className="border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500 hover:text-white font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-300">
                    View Case Studies
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-cyan-400 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content - Visual Element */}
            <div className="relative hidden lg:block">
              <div className="relative w-full h-[600px]">
                {/* Glassmorphism Cards */}
                <div className="absolute top-0 right-0 w-80 p-6 bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-500">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-lg flex items-center justify-center">
                      <TrendingUp size={24} className="text-white" />
                    </div>
                    <div>
                      <div className="text-white font-semibold">Campaign Performance</div>
                      <div className="text-green-400 text-sm">+150% Growth</div>
                    </div>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full w-3/4 bg-gradient-to-r from-cyan-500 to-teal-600 rounded-full"></div>
                  </div>
                </div>

                <div className="absolute bottom-20 left-0 w-80 p-6 bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-500">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-green-600 rounded-lg flex items-center justify-center">
                      <Target size={24} className="text-white" />
                    </div>
                    <div>
                      <div className="text-white font-semibold">ROI Metrics</div>
                      <div className="text-green-400 text-sm">3.5x Average Return</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <div className="text-cyan-400 font-bold">200+</div>
                      <div className="text-xs text-gray-400">Campaigns</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <div className="text-cyan-400 font-bold">50+</div>
                      <div className="text-xs text-gray-400">Clients</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <div className="text-cyan-400 font-bold">8+</div>
                      <div className="text-xs text-gray-400">Years</div>
                    </div>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-br from-cyan-500/20 to-teal-600/20 rounded-full blur-3xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 opacity-50"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              Complete Golf Marketing Solutions
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              From strategy to execution, we provide comprehensive marketing services tailored for the golf industry
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card 
                  key={service.id}
                  className="bg-slate-800/50 border-slate-700 hover:border-cyan-500/50 backdrop-blur-sm transition-all duration-300 hover:transform hover:scale-105 group"
                >
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <Icon size={28} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-slate-950 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-cyan-400 font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
                15+ Years of Golf Industry Expertise
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                We're not just marketers – we're golf industry specialists. Our deep understanding of apps, websites, talent management, golf clubs, resorts, and media publications sets us apart.
              </p>
              
              <div className="space-y-4">
                {[
                  'Proven track record with 200+ successful campaigns',
                  'Industry-specific knowledge and connections',
                  'Data-driven strategies with measurable results',
                  'Full-service marketing from strategy to execution',
                  'Customized solutions for your unique needs'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={24} />
                    <span className="text-gray-300 text-lg">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/contact" className="inline-block mt-8">
                <Button className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white font-semibold px-8 py-6 text-lg rounded-xl shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300">
                  Let's Talk About Your Goals
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_maxgolf-site/artifacts/rzjx8fji_IMG_8995.jpg" 
                  alt="The Open Championship professional golf"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent"></div>
              </div>
              
              {/* Floating Stats Card */}
              <div className="absolute -bottom-8 -left-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 shadow-2xl">
                <div className="text-3xl font-bold text-cyan-400 mb-2">3.5x</div>
                <div className="text-white font-semibold">Average ROI</div>
                <div className="text-sm text-gray-400">for our clients</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-cyan-600 via-teal-600 to-green-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIuNSIgb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Maximize Your Golf Marketing?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Let's discuss how we can help your golf business reach its full potential with proven marketing strategies.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-cyan-600 hover:bg-gray-100 font-bold px-10 py-6 text-lg rounded-xl shadow-2xl hover:shadow-white/30 transition-all duration-300 group">
              Start Your Journey
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
